import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import axios from '../../../../api/axios';
import {
  CONTRACTS_AUTOMATION_ENDPOINT,
  CONTRACTS_AUTOMATION_BULK_UPDATE_ENDPOINT,
  TENANT_SETTINGS_ENDPOINT,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
} from '../../../../consts';
import { ContractAutomationItem, ContractAutomationLevelValue } from '../../../../types/contracts';
import { useHasPermission } from '../../../../contexts/auth';
import styles from './tenantSettlementAutomation.module.css';

// The backend represents "no automation" as null; the Select can't take a null
// value directly, so the empty string stands in for it in the UI only.
const MANUAL_VALUE = '';

const AUTOMATION_LEVEL_OPTIONS: { value: ContractAutomationLevelValue | typeof MANUAL_VALUE; label: string }[] = [
  { value: MANUAL_VALUE, label: 'Manual' },
  { value: 'C', label: 'Auto-confirm settlements' },
  { value: 'I', label: 'Auto-confirm and invoice' },
  { value: 'F', label: 'Auto-confirm, invoice and send' },
];

const toUiValue = (level: ContractAutomationLevelValue | null): string => level ?? MANUAL_VALUE;
const fromUiValue = (value: string): ContractAutomationLevelValue | null =>
  value === MANUAL_VALUE ? null : (value as ContractAutomationLevelValue);

// ISO date strings ("YYYY-MM-DD") compare correctly lexicographically.
const isContractActive = (endDate: string | null): boolean =>
  !endDate || endDate >= new Date().toISOString().slice(0, 10);

const SCROLL_THRESHOLD_PX = 24;
const SEARCH_DEBOUNCE_MS = 300;
// Wide enough to fit the longest option ("Auto-confirm, invoice and send")
// without the Select resizing itself based on which option is selected.
const SELECT_MIN_WIDTH = 420;

const buildListUrl = (search: string): string => {
  const trimmed = search.trim();
  if (!trimmed) {
    return CONTRACTS_AUTOMATION_ENDPOINT;
  }

  const params = new URLSearchParams({ search: trimmed });
  return `${CONTRACTS_AUTOMATION_ENDPOINT}?${params.toString()}`;
};

export const TenantSettlementAutomation = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ContractAutomationItem[]>([]);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [pendingChanges, setPendingChanges] = useState<Record<number, ContractAutomationLevelValue | null>>({});
  const [automationRunDay, setAutomationRunDay] = useState<number | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canView = useHasPermission('settlement.view_contract');
  const canChange = useHasPermission('settlement.change_contract');

  const fetchPage = useCallback(
    async (url: string, replace: boolean) => {
      if (replace) {
        setInitialLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const { data } = await axios.get(url);
        setItems((prev) => (replace ? data.results : [...prev, ...data.results]));
        setNextUrl(data.next);
        if (replace) {
          setPendingChanges({});
        }
      } catch (error) {
        enqueueSnackbar(t('Failed to fetch contracts'), ERROR_SNACKBAR);
      } finally {
        if (replace) {
          setInitialLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [enqueueSnackbar, t]
  );

  useEffect(() => {
    const fetchAutomationRunDay = async () => {
      try {
        const { data } = await axios.get(`${TENANT_SETTINGS_ENDPOINT}settings/`);
        setAutomationRunDay(data?.automation_run_day ?? null);
      } catch (error) {
        setAutomationRunDay(null);
      }
    };

    fetchAutomationRunDay();
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchPage(buildListUrl(search), true);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!nextUrl || loadingMore || initialLoading) {
      return;
    }

    const target = event.currentTarget;
    const reachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - SCROLL_THRESHOLD_PX;

    if (reachedBottom) {
      fetchPage(nextUrl, false);
    }
  };

  const handleLevelChange = (
    contractId: number,
    currentLevel: ContractAutomationLevelValue | null,
    nextLevel: ContractAutomationLevelValue | null
  ) => {
    setPendingChanges((prev) => {
      const next = { ...prev };
      if (nextLevel === currentLevel) {
        delete next[contractId];
      } else {
        next[contractId] = nextLevel;
      }
      return next;
    });
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const handleSave = async () => {
    const payload = Object.entries(pendingChanges).map(([id, automation_level]) => ({
      id: Number(id),
      automation_level,
    }));

    if (payload.length === 0) {
      return;
    }

    setSaving(true);
    try {
      await axios.patch(CONTRACTS_AUTOMATION_BULK_UPDATE_ENDPOINT, payload);
      enqueueSnackbar(t('Automation settings were successfully saved.'), SUCCESS_SNACKBAR);
      fetchPage(buildListUrl(search), true);
    } catch (error) {
      enqueueSnackbar(t('There was an error, please try again.'), ERROR_SNACKBAR);
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return null;
  }

  return (
    <Box className={styles.container}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('Select the automation level for each contract.')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {automationRunDay
          ? t('Automation runs on day {{day}} of each month.', { day: automationRunDay })
          : t('The automation day is not configured yet. Set it under Settings.')}
      </Typography>

      <TextField
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('Search by contract number or client name')}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {initialLoading ? (
        <Box className={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('No contracts found')}
          </Typography>
        </Box>
      ) : (
        <Paper
          className={styles.listPaper}
          sx={{ maxHeight: 480, overflow: 'auto' }}
          onScroll={handleScroll}
        >
          <List disablePadding>
            {items.map((contract, index) => {
              const effectiveLevel =
                contract.id in pendingChanges ? pendingChanges[contract.id] : contract.automation_level;
              const active = isContractActive(contract.end_date);

              return (
                <React.Fragment key={contract.id}>
                  <ListItem className={styles.listItem}>
                    <Box className={styles.itemInfo}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {[contract.number, contract.client?.name].filter(Boolean).join(' - ')}
                        </Typography>
                        <Chip
                          label={active ? t('Active') : t('Inactive')}
                          size="small"
                          sx={{
                            bgcolor: active ? 'success.light' : 'warning.light',
                            color: active ? 'success.contrastText' : 'warning.contrastText',
                            fontWeight: 600,
                            '& .MuiChip-label': {
                              color: active ? 'success.contrastText' : 'warning.contrastText',
                            },
                          }}
                        />
                      </Box>
                      {contract.internal_name && (
                        <Typography variant="body2" color="text.secondary">
                          {contract.internal_name}
                        </Typography>
                      )}
                    </Box>
                    <FormControl
                      size="small"
                      disabled={!canChange}
                      sx={{ minWidth: { xs: '100%', sm: SELECT_MIN_WIDTH }, width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
                    >
                      <Select
                        value={toUiValue(effectiveLevel)}
                        displayEmpty
                        onChange={(event: SelectChangeEvent) =>
                          handleLevelChange(
                            contract.id,
                            contract.automation_level,
                            fromUiValue(event.target.value)
                          )
                        }
                      >
                        {AUTOMATION_LEVEL_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {t(option.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </ListItem>
                  {index < items.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Paper>
      )}

      {canChange && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="contained" color="success" disabled={!hasChanges || saving} onClick={handleSave}>
            {saving ? t('Saving...') : t('Save')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
